import {assert} from 'Asserts';
import {Sequence as SequenceType} from 'components/Sequence';
import ProteinType from 'molbio/aa-sequences/AaSequence';
import {TabState, TabStateDefaults} from 'Navigation';

export interface PolymerTabStateAttributes<
  Polymer extends SequenceType | ProteinType,
> extends TabStateDefaults {
  polymer: Polymer;
  sequence: Polymer;
  protein: Polymer;
}

interface PolymerTabState<Polymer extends SequenceType | ProteinType> {
  get<K extends keyof PolymerTabStateAttributes<Polymer>>(key: K): PolymerTabStateAttributes<Polymer>[K];
  set<K extends keyof PolymerTabStateAttributes<Polymer>>(
    attributeName: K,
    value: PolymerTabStateAttributes<Polymer>[K],
    options?: Backbone.ModelSetOptions
  ): PolymerTabState<Polymer>;
  set(obj: Partial<PolymerTabStateAttributes<Polymer>>): PolymerTabState<Polymer>;
}

/**
 * Abstract base class model for a tab backed by a polymer, either Sequence or Protein.
 *
 * Note that it is generic across polymers in the same manner as SinglePolymerMapState.
 */
abstract class PolymerTabState<Polymer extends SequenceType | ProteinType> extends TabState {
  override initialize(attributes?: {}, options?: {}): void {
    super.initialize(attributes, options);
    this.setPolymer();
    this.on('change:sequence change:polymer change:protein', this.setPolymer.bind(this));
  }

  setPolymer(): void {
    const polymer = this.polymer() || this.get('sequence') || this.get('protein');
    assert(polymer != null, 'You must define a polymer for this tab state.');
    this.set('sequence', polymer);
    this.set('polymer', polymer);
    this.set('protein', polymer);
  }

  isProteinTabState(): this is PolymerTabState<ProteinType> {
    // Inline import to address circular import issue.
    const Protein = require('molbio/aa-sequences/AaSequence').default;
    return this.polymer() instanceof Protein;
  }

  isSequenceTabState<TSequence extends SequenceType>(): this is PolymerTabState<TSequence> {
    // Inline import to address circular import issue.
    const Sequence = require('components/Sequence').Sequence;
    return this.polymer() instanceof Sequence;
  }

  // We use polymer to refer to either a Protein or a Sequence.
  polymer(): Polymer {
    return this.get('polymer');
  }

  sequence<TSequence extends SequenceType>(this: PolymerTabState<TSequence>): TSequence {
    return this.polymer();
  }

  protein(this: PolymerTabState<ProteinType>): ProteinType {
    return this.polymer();
  }
}

export {PolymerTabState};
